import { getDownloadURL, getStorage, ref, uploadString } from 'firebase/storage';
import { GOLD_ENABLE_FIREBASE_UPLOAD, SEGMIND_GOLD } from '../config/gold';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Downscale + JPEG encode so workflow POST stays under Segmind size limits.
 */
export function compressDataUrl(
  dataUrl,
  { maxEdge = SEGMIND_GOLD.maxImageEdge, quality = SEGMIND_GOLD.jpegQuality } = {}
) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > maxEdge) {
        const scale = maxEdge / longest;
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = dataUrl;
  });
}

async function uploadTempToFirebase(dataUrl) {
  const storage = getStorage();
  const storageRef = ref(storage, `gold_temp/${Date.now()}.jpg`);
  await uploadString(storageRef, dataUrl, 'data_url');
  return getDownloadURL(storageRef);
}

/** URL for Segmind (Firebase temp only when uploads enabled) or inline base64 */
export async function dataUrlToSegmindInput(dataUrl) {
  let compressed = await compressDataUrl(dataUrl);

  if (GOLD_ENABLE_FIREBASE_UPLOAD) {
    try {
      return await uploadTempToFirebase(compressed);
    } catch (err) {
      console.warn('Segmind temp Firebase upload failed, using inline data', err);
    }
  }

  if (compressed.length > SEGMIND_GOLD.maxInlineBase64Chars) {
    compressed = await compressDataUrl(dataUrl, {
      maxEdge: 768,
      quality: 0.72,
    });
  }

  if (compressed.length > SEGMIND_GOLD.maxInlineBase64Chars) {
    throw new Error(
      'Photo is too large to process without cloud upload. Retake or enable Firebase upload.'
    );
  }

  return compressed;
}

/** @deprecated Use dataUrlToSegmindInput */
export async function dataUrlToPublicUrl(dataUrl) {
  return dataUrlToSegmindInput(dataUrl);
}

export async function urlToDataUrl(url) {
  const response = await fetch(url, { cache: 'no-store' });
  const blob = await response.blob();
  const mime = blob.type || 'image/png';
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string' && !result.startsWith('data:')) {
        resolve(`data:${mime};base64,${result}`);
      } else {
        resolve(result);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function formatSegmindFailure(result) {
  const err = result?.error_message;
  if (typeof err === 'string') return err;
  if (err?.error_message) return err.error_message;
  if (err?.details) return err.details;
  return result?.message || 'Segmind workflow failed';
}

function parseWorkflowOutput(result) {
  if (!result?.output) return null;
  try {
    const outputs = JSON.parse(result.output);
    const imageEntry = outputs.find(
      (item) => item?.value?.type === 'image' && item?.value?.data
    );
    return imageEntry?.value?.data || null;
  } catch (e) {
    console.warn('Failed to parse Segmind output', e);
    return null;
  }
}

export async function pollSegmindRequest(pollUrl) {
  const { apiKey, pollIntervalMs, maxPollAttempts } = SEGMIND_GOLD;

  for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
    const response = await fetch(pollUrl, {
      method: 'GET',
      headers: { 'x-api-key': apiKey },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Segmind poll request failed');
    }

    const result = await response.json();

    if (result.status === 'COMPLETED') {
      const imageUrl = parseWorkflowOutput(result);
      if (!imageUrl) {
        throw new Error('Segmind completed without image output');
      }
      return imageUrl;
    }

    if (result.status === 'FAILED') {
      throw new Error(formatSegmindFailure(result));
    }

    await sleep(pollIntervalMs);
  }

  throw new Error('Segmind workflow timed out');
}

export function buildSegmindWorkflowPayload(imageInput) {
  const { workflowImageField } = SEGMIND_GOLD;
  return { [workflowImageField]: imageInput };
}

export async function removeBackgroundWithWorkflow(imageInput) {
  const { apiKey, workflowUrl } = SEGMIND_GOLD;
  const payload = buildSegmindWorkflowPayload(imageInput);

  let response;
  try {
    response = await fetch(workflowUrl, {
      method: 'POST',
      headers: {
        'x-api-key': apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    const hint =
      err?.message === 'Failed to fetch'
        ? ' Cannot reach api.segmind.com (check internet/DNS/firewall on this device).'
        : '';
    throw new Error(`Segmind request failed:${hint} ${err?.message || err}`);
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Segmind workflow submit failed');
  }

  const result = await response.json();

  if (result.status === 'COMPLETED') {
    const outputUrl = parseWorkflowOutput(result);
    if (outputUrl) return outputUrl;
  }

  if (result.poll_url) {
    return pollSegmindRequest(result.poll_url);
  }

  if (result.status === 'FAILED') {
    throw new Error(formatSegmindFailure(result));
  }

  throw new Error(result.message || 'Unexpected Segmind response');
}

export async function removeBackgroundFromDataUrl(dataUrl) {
  const imageInput = await dataUrlToSegmindInput(dataUrl);
  const cutoutUrl = await removeBackgroundWithWorkflow(imageInput);
  return urlToDataUrl(cutoutUrl);
}
